import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, Compass, Lock, Clock } from 'lucide-react';

export const AboutSection = () => {
  const { t } = useTranslation();

  const features = [
    {
      icon: MapPin,
      title: t('about.expertise'),
      description: t('about.expertiseDesc'),
    },
    {
      icon: Compass,
      title: t('about.bespoke'),
      description: t('about.bespokeDesc'),
    },
    {
      icon: Lock,
      title: t('about.access'),
      description: t('about.accessDesc'),
    },
    {
      icon: Clock,
      title: t('about.support'),
      description: t('about.supportDesc'),
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-amber-50 to-white" id="about">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-serif text-stone-800 mb-6">
              {t('about.title')}
            </h2>
            <p className="text-lg text-stone-600 mb-8 leading-relaxed">
              {t('about.subtitle')}
            </p>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-800 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-stone-600">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Image Grid */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden h-48">
                  <img
                    src="https://images.unsplash.com/photo-1609130787305-826ad0e60af7"
                    alt="Balinese Culture"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-64">
                  <img
                    src="https://images.unsplash.com/photo-1554689021-c9e70753d301"
                    alt="Rice Terraces"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden h-64">
                  <img
                    src="https://images.unsplash.com/photo-1697898109604-e06e88b15271"
                    alt="Luxury Villa"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-48">
                  <img
                    src="https://images.unsplash.com/photo-1583297016081-23a9753cd364"
                    alt="Diving Experience"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            
            {/* Decorative Element */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-amber-200/50 rounded-full blur-2xl" />
            <div className="absolute -top-4 -right-4 w-32 h-32 bg-teal-200/30 rounded-full blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
